import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { OnTimeDelivery } from '../../../../types/dashboard.types';
import { useTheme } from '../../../theme/ThemeProvider';
import Tooltip from '../../../components/ui/Tooltip/Tooltip';
import { chartColors } from '../chartTheme';
import { dashboardService, OtdRange } from '../../../../services/dashboardService';
import styles from './OtdCard.module.css';

interface OtdCardProps {
  /** Valor inicial provisto por el dashboard (fallback mientras carga el filtro). */
  data?: OnTimeDelivery;
  /** Cambia cuando el usuario pulsa Refresh en el header, para recargar el OTD. */
  refreshKey?: number;
}

const RANGES: { key: OtdRange; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'all', label: 'All' },
];

const OTD_HELP =
  'Percentage of completed shipments delivered on or before their Protect date, for the selected period. Only completed shipments that have both a delivery date and a Protect date are counted. The period filters by delivery date.';

const InfoIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const OtdCard: React.FC<OtdCardProps> = ({ data: initialData, refreshKey }) => {
  const { resolvedTheme } = useTheme();
  const [range, setRange] = React.useState<OtdRange>('month');
  const [data, setData] = React.useState<OnTimeDelivery | undefined>(undefined);
  const [loading, setLoading] = React.useState(true);

  // Recarga el OTD cuando cambia el filtro (default: mes actual) o al pulsar Refresh.
  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    dashboardService
      .getOtd(range)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch(() => {
        if (alive) setData(undefined);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [range, refreshKey]);

  const shown = data ?? initialData;
  const rate = shown?.on_time_rate ?? 0;
  const rounded = Math.round(rate);
  const track = resolvedTheme === 'dark' ? '#333333' : '#eceff3';

  const pieData = [
    { name: 'on-time', value: rate },
    { name: 'rest', value: Math.max(0, 100 - rate) },
  ];

  return (
    <div className={[styles.card, loading ? 'dylo-loading-host' : ''].join(' ')}>
      {loading && <span className="dylo-loading-sweep" aria-label="Loading" />}
      <div className={styles.filter}>
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={[styles.filterBtn, range === r.key ? styles.filterActive : ''].join(' ')}
            onClick={() => setRange(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        <div className={styles.donut}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                innerRadius={30}
                outerRadius={42}
                startAngle={90}
                endAngle={-270}
                stroke="none"
                isAnimationActive={false}
              >
                <Cell fill={chartColors.otd} />
                <Cell fill={track} />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <span className={styles.donutLabel}>{rounded}%</span>
        </div>

        <div className={styles.info}>
          <p className={styles.title}>
            On-Time Delivery Rate (OTD)
            <Tooltip content={OTD_HELP} placement="left" multiline>
              <span className={styles.infoIcon}>
                <InfoIcon />
              </span>
            </Tooltip>
          </p>
          <p className={styles.rate}>{rate.toFixed(2)}%</p>
          <p className={styles.detail}>
            {shown?.on_time_delivered ?? 0} of {shown?.total_delivered ?? 0} deliveries on time
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtdCard;
